import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, CheckBadgeIcon } from '@heroicons/react/20/solid';
import { useState, useEffect, useRef } from "react";
import { addDays, startOfWeek, format, isToday } from "date-fns";
import ManagerNav from './managernav';
import ProcessViewer from './processviewer';
import TaskCalendarTeamView from './taskcalendarteamview';
import TaskGraph from './taskgraph';

// Add global styles for animations
const fadeInAnimation = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

// Add style tag to document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = fadeInAnimation;
  document.head.appendChild(style);
}


// Task card to display available tasks
const TaskCard = ({ task }) => {
  // Function to generate a consistent gradient for each processName
  const getGradientForProcess = (processName = "") => {
    if (!processName) return "from-gray-500 to-gray-600"; // Default gradient for undefined names

    const gradients = [
      "from-red-500 to-orange-500",
      "from-blue-500 to-indigo-600",
      "from-green-500 to-emerald-600",
      "from-yellow-500 to-amber-600",
      "from-purple-500 to-pink-500",
      "from-pink-500 to-rose-500",
      "from-teal-500 to-cyan-600",
      "from-orange-500 to-amber-500"
    ];
    
    let hash = 0;
    for (let i = 0; i < processName.length; i++) {
      hash = processName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length]; // Assign a gradient consistently
  };

  return (
    <div
      className={`p-3 shadow-md rounded-lg mb-3 transform transition-all duration-200 hover:-translate-y-1 hover:shadow-lg bg-gradient-to-r ${getGradientForProcess(task.processName)} relative`}
    >
      <div className="flex items-center">
        {task.step && (
          <div className="rounded-full mr-3 bg-white w-6 h-6 text-xs font-bold flex items-center justify-center shadow-md">
            {task.step}
          </div>
        )}
        <div className="flex flex-col text-white">
          <span className="font-semibold text-sm">{task.name}</span>
          <span className="text-xs opacity-90">{task.processName || "Unknown Process"}</span>
        </div>
      </div>
    </div>
  );
};



// TimeSlot Component with dropdown for task selection
const TimeSlot = ({ time, date, onTaskSelect, isMissing, assignedTask, availableTasks, breakLength }) => {
  const [prevTasks, setPrevTasks] = useState(null);
  const [openPrevTasks, setOpenPrevTasks] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showBreakInputs, setShowBreakInputs] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState('');
  const [breakEndTime, setBreakEndTime] = useState('');
  const [additionalTask, setAdditionalTask] = useState(null);
  const [showAdditionalTaskDropdown, setShowAdditionalTaskDropdown] = useState(false);

  // Function to fetch previous tasks when button is clicked
  const fetchPrevTasks = async () => {
    if (!assignedTask) return; // Prevent API call if no task is assigned

    try {
      const response = await fetch("https://api.insightos.co.uk/api/getPreviousTasks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({
          taskId: assignedTask.id,
          taskStep: assignedTask.step,
          date,
          time,
          ProcessId: assignedTask.processId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Previous tasks fetched:", data);
        setPrevTasks(data.data);
        setOpenPrevTasks(true); // Open the modal only after fetching tasks
      } else {
        console.error("Failed to fetch previous tasks");
      }
    } catch (error) {
      console.error("Error fetching previous tasks:", error);
    }
  };

  const handleTaskChange = (e) => {
    const selectedTaskIndex = e.target.value;
    if (selectedTaskIndex === "") {
      // Handle the case where "Select Task" is chosen (clear the task)
      onTaskSelect({}, time, date);
      setShowBreakInputs(false);
      setShowAdditionalTaskDropdown(false);
      setAdditionalTask(null);
    } else {
      // Find the selected task from available tasks
      const selectedTask = availableTasks[selectedTaskIndex];
      
      // Check if it's a break task and break length < 60
      if (selectedTask.name === "Break" && breakLength < 60) {
        setShowBreakInputs(true);
        setShowAdditionalTaskDropdown(true);
      } else {
        setShowBreakInputs(false);
        setShowAdditionalTaskDropdown(false);
        setAdditionalTask(null);
        onTaskSelect(selectedTask, time, date);
      }
    }
    setDropdownOpen(false);
  };

  // Handle additional task selection (for when break < 60 minutes)
  const handleAdditionalTaskChange = (e) => {
    const selectedTaskIndex = e.target.value;
    if (selectedTaskIndex === "") {
      setAdditionalTask(null);
    } else {
      const selectedTask = availableTasks[selectedTaskIndex];
      // Filter out Break task from additional tasks
      if (selectedTask.name !== "Break") {
        setAdditionalTask(selectedTask);
      }
    }
  };

  // Handle break time submission
  const handleBreakSubmit = () => {
    if (!breakStartTime || !breakEndTime) {
      alert('Please enter both start and end times for the break');
      return;
    }

    // Calculate break duration in minutes
    const [startHour, startMinute] = breakStartTime.split(":").map(Number);
    const [endHour, endMinute] = breakEndTime.split(":").map(Number);
    const startDate = new Date(1970, 0, 1, startHour, startMinute);
    const endDate = new Date(1970, 0, 1, endHour, endMinute);
    let duration = Math.round((endDate - startDate) / (1000 * 60));
    if (duration < 0) duration += 1440; // handle overnight breaks

    const breakTask = {
      name: "Break",
      step: "N/A",
      isBreak: true,
      breakStartTime,
      breakEndTime,
      breakDuration: duration,
      additionalTask: additionalTask // Include additional task if selected
    };

    onTaskSelect(breakTask, time, date);
    setShowBreakInputs(false);
    setShowAdditionalTaskDropdown(false);
    setBreakStartTime('');
    setBreakEndTime('');
    setAdditionalTask(null);
  };

  // Function to generate consistent color for each task (background color)
  const getTaskColor = (processName = "") => {
    if (!processName) return "bg-gray-50 border-gray-300"; // Default color for undefined names

    const colors = [
      "bg-red-50 border-red-500", 
      "bg-blue-50 border-blue-500", 
      "bg-green-50 border-green-500", 
      "bg-yellow-50 border-yellow-500",
      "bg-purple-50 border-purple-500", 
      "bg-pink-50 border-pink-500", 
      "bg-teal-50 border-teal-500", 
      "bg-orange-50 border-orange-500",
    ];
    
    let hash = 0;
    for (let i = 0; i < processName.length; i++) {
      hash = processName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length]; // Assign a color consistently
  };

  // Function to get text color matching the border color
  const getTextColor = (processName = "") => {
    if (!processName) return "text-gray-700"; // Default color for undefined names

    const colors = [
      "text-red-700", 
      "text-blue-700", 
      "text-green-700", 
      "text-yellow-700",
      "text-purple-700", 
      "text-pink-700", 
      "text-teal-700", 
      "text-orange-700",
    ];
    
    let hash = 0;
    for (let i = 0; i < processName.length; i++) {
      hash = processName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length]; // Assign a color consistently
  };
  
  // Function to get dropdown background color
  const getDropdownBgColor = (processName = "") => {
    if (!processName) return "from-indigo-600 to-purple-600"; // Default gradient

    const gradients = [
      "from-red-500 to-orange-500",
      "from-blue-500 to-indigo-600",
      "from-green-500 to-emerald-600",
      "from-yellow-500 to-amber-600",
      "from-purple-500 to-pink-500",
      "from-pink-500 to-rose-500",
      "from-teal-500 to-cyan-600",
      "from-orange-500 to-amber-500"
    ];
    
    let hash = 0;
    for (let i = 0; i < processName.length; i++) {
      hash = processName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return gradients[Math.abs(hash) % gradients.length];
  };

  return (
    <div
      className="flex flex-col items-center mt-2 relative"
      data-component="task-calendar-slot"
      data-slot-time={time}
      data-slot-date={format(date, "yyyy-MM-dd")}
    >
      <div className="text-xs font-semibold bg-gray-100 rounded-full px-2 py-0.5 text-indigo-600">{time}</div>
      
      {/* Main task container */}
      <div
        className={`w-full relative border-l-4 mt-1 ${
          assignedTask?.name
            ? `${getTaskColor(assignedTask.processName)}`
            : isMissing
            ? "bg-red-50 border-red-500"
            : "bg-gray-50 border-gray-300"
        } rounded-md shadow-md transition-all duration-300 hover:shadow-lg`}
        style={{ position: 'relative', zIndex: dropdownOpen ? '45' : '1' }}
      >
        {/* Click area for opening dropdown */}
        <div 
          onClick={() => setDropdownOpen(!dropdownOpen)}
          data-feature="toggle-slot-dropdown"
          className={`w-full p-2 cursor-pointer flex justify-between items-center ${
            assignedTask?.name ? getTextColor(assignedTask.processName) : "text-gray-500"
          }`}
        >
          <span className={`text-sm font-medium`}>
            {assignedTask 
              ? assignedTask.isBreak 
                ? `${assignedTask.name} (${assignedTask.breakStartTime}-${assignedTask.breakEndTime})${assignedTask.additionalTask ? ` + ${assignedTask.additionalTask.name}` : ''}`
                : `${assignedTask.name}${assignedTask.step ? ` (Step ${assignedTask.step})` : ''}`
              : "Select Task"
            }
          </span>
          <ChevronDownIcon className="h-5 w-5" />
        </div>
        
        {/* Blur overlay when dropdown is open */}
        {dropdownOpen && (
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-md z-50"
            data-component="task-calendar-slot-overlay"
            onClick={() => setDropdownOpen(false)}
          ></div>
        )}
        
        {/* Task dropdown - always centered on screen for all devices */}
        {dropdownOpen && (
          <div 
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[60] bg-white border-0 rounded-xl shadow-2xl overflow-hidden w-[95%] max-w-lg"
            style={{ 
              overflowY: 'auto',
              maxHeight: '85vh',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              animation: 'fadeIn 0.2s ease-out',
            }}
          >
            {/* Gradient header for dropdown */}
            <div className={`bg-gradient-to-r ${getDropdownBgColor(assignedTask?.processName)} p-3 sticky top-0 text-white text-sm font-bold flex justify-between items-center`}>
              <span>Select Task for {time}</span>
              <button 
                className="h-6 w-6 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-white hover:bg-opacity-30 transition-colors"
                data-feature="close-slot-dropdown"
                onClick={() => setDropdownOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <ul className="py-2 divide-y divide-gray-100">
              <li 
                className="px-4 py-3 hover:bg-red-50 cursor-pointer flex items-center text-red-600 font-medium transition-colors"
                data-feature="clear-slot-selection"
                onClick={() => {
                  onTaskSelect({}, time, date);
                  setDropdownOpen(false);
                }}
              >
                <span className="rounded-full mr-2 bg-red-100 w-6 h-6 text-xs font-bold text-red-600 flex items-center justify-center">
                  ✕
                </span>
                Clear Selection
              </li>
              
              {availableTasks.map((task, index) => {
                // Generate a color for this task
                const isCurrentTask = assignedTask?.name === task.name;
                const taskColorClass = getDropdownBgColor(task.processName);
                
                return (
                  <li 
                    key={index}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-200 ${
                      isCurrentTask ? "bg-gray-100" : ""
                    }`}
                    data-feature="assign-slot-task"
                    data-task-name={task.name}
                    onClick={() => {
                      // Special handling for Break task
                      if (task.name === "Break" && breakLength < 60) {
                        setShowBreakInputs(true);
                        setDropdownOpen(false);
                      } else {
                        onTaskSelect(task, time, date);
                        setDropdownOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      {/* Step indicator with dynamic color */}
                      {task.step && (
                        <span className={`rounded-full mr-3 min-w-[2rem] h-8 text-sm font-bold text-white flex items-center justify-center bg-gradient-to-r ${taskColorClass} shadow-sm`}>
                          {task.step}
                        </span>
                      )}
                      
                      {/* Task name with dynamic color */}
                      <div className="flex flex-col">
                        <span className="font-medium text-base">{task.name}</span>
                        {task.processName && (
                          <span className="text-xs text-gray-600 mt-0.5">
                            {task.processName}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Break time inputs - shown when Break is selected and break length < 60 */}
      {showBreakInputs && (
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="text-sm font-medium text-blue-800 mb-2">Break Time Details</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="block text-xs text-blue-700 mb-1">Start Time</label>
              <input
                type="time"
                value={breakStartTime}
                onChange={(e) => setBreakStartTime(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-blue-700 mb-1">End Time</label>
              <input
                type="time"
                value={breakEndTime}
                onChange={(e) => setBreakEndTime(e.target.value)}
                className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          
          {/* Additional task selection for breaks < 60 minutes */}
          <div className="mb-3">
            <label className="block text-xs text-blue-700 mb-1">Additional Task (Optional)</label>
            <select
              value={additionalTask ? availableTasks.findIndex(task => task.id === additionalTask.id) : ""}
              onChange={handleAdditionalTaskChange}
              className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select Additional Task</option>
              {availableTasks
                .filter(task => task.name !== "Break") // Filter out Break from additional tasks
                .map((task, index) => {
                  const originalIndex = availableTasks.findIndex(t => t.id === task.id);
                  return (
                    <option key={task.id} value={originalIndex}>
                      {task.name}
                      {task.step && ` (Step ${task.step})`}
                    </option>
                  );
                })
              }
            </select>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleBreakSubmit}
              data-feature="submit-slot-break"
              className="flex-1 px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
            >
              Set Break
            </button>
            <button
              onClick={() => {
                setShowBreakInputs(false);
                setShowAdditionalTaskDropdown(false);
                setBreakStartTime('');
                setBreakEndTime('');
                setAdditionalTask(null);
              }}
              data-feature="cancel-slot-break"
              className="flex-1 px-3 py-1 bg-gray-400 text-white text-xs font-medium rounded hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-1 flex flex-wrap justify-center gap-1">
        {assignedTask && (
          <button 
            className={`text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium shadow-sm hover:shadow transition-all`}
            data-feature="view-previous-slot_post"
            onClick={fetchPrevTasks}
          >
            View Previous
          </button>
        )}
        
        {isMissing && (
          <span className="text-white text-xs bg-red-500 px-2 py-0.5 rounded-full shadow-sm">
            Missing Task
          </span>
        )}
      </div>

      {/* Previous tasks modal with blur effect */}
      {openPrevTasks && prevTasks && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center p-4">
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto animate-fadeIn"
            style={{
              animation: "fadeIn 0.3s ease-out",
              transform: "translateZ(0)",
            }}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white">Previous Tasks</h3>
                <button 
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full h-7 w-7 flex items-center justify-center transition-colors"
                  data-feature="close-previous-tasks"
                  onClick={() => setOpenPrevTasks(false)}
                >
                  &times;
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {prevTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-6 text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-center">No previous tasks found.</p>
                </div>
              ) : (
                prevTasks.map((task, idx) => {
                  // Generate a gradient for each task card
                  const gradients = [
                    "from-blue-50 to-indigo-50 border-l-4 border-indigo-500",
                    "from-green-50 to-emerald-50 border-l-4 border-green-500",
                    "from-purple-50 to-pink-50 border-l-4 border-purple-500",
                    "from-yellow-50 to-amber-50 border-l-4 border-yellow-500",
                    "from-red-50 to-orange-50 border-l-4 border-red-500",
                  ];
                  const gradient = gradients[idx % gradients.length];
                  
                  return (
                    <div 
                      key={idx} 
                      className={`bg-gradient-to-r ${gradient} p-4 rounded-md shadow-md mb-3 transform transition-all hover:-translate-y-1 hover:shadow-lg`}
                    >
                      <div className="flex justify-between items-start">
                        <p className="text-sm font-bold text-gray-800">{task.Task}</p>
                        <span className="bg-indigo-100 text-indigo-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                          Step {task.Step}
                        </span>
                      </div>
                      
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-700">
                        <div>
                          <p className="text-gray-500">Start Time</p>
                          <p className="font-semibold">{task.StartTime}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Task Date</p>
                          <p className="font-semibold">{format(new Date(task.TaskDate), "MMM dd, yyyy")}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Staff Role</p>
                          <p className="font-semibold">{task.Role}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Staff Name</p>
                          <p className="font-semibold">{task.Name}</p>
                        </div>
                      </div>
                      
                      <div className="mt-2 flex items-center">
                        <span className="text-gray-500 text-xs mr-2">Completed:</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          task.Completed === "Yes" 
                            ? "bg-green-100 text-green-800" 
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {task.Completed}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <div className="p-4 border-t">
              <button
                className="w-full py-2 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-medium rounded-md shadow-md transition-all"
                data-feature="dismiss-previous-tasks"
                onClick={() => setOpenPrevTasks(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

 
  

export default function TaskCalendar() {
  const container = useRef(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(
    addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 7)
  );
  
  const [staff, setStaff] = useState([]);
  const [staffMember, setStaffMember] = useState("");
  const [shifts, setShifts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState({});
  const [noMissingTimes, setNoMissingTimes] = useState(true);
  const [missingDetails, setMissingDetails] = useState([]);
  const [noShift, setNoShift] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [openTaskGraph, setOpenTaskGraph] = useState(false);
  const [breakLength, setBreakLength] = useState(0);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [overwrittenTasks, setOverwrittenTasks] = useState([]);

  const fetchOverwrittenTasks = async () => {
    try {
      const res = await fetch('https://api.insightos.co.uk/api/overwritten-tasks', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setOverwrittenTasks(data.data || []);
      }
    } catch (e) {
      console.error('Failed to fetch overwritten tasks', e);
    }
  };

  const dismissOverwrittenTask = async (taskID) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/deletetask/${taskID}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setOverwrittenTasks(prev => prev.filter(t => t.TaskID !== taskID));
    } catch (e) {
      console.error('Failed to dismiss overwritten task', e);
    }
  };

  useEffect(() => {
    fetchOverwrittenTasks();
  }, []);

  const weekDates = Array.from({ length: 7 }, (_, i) =>
    addDays(currentWeekStart, i)
  );

  const saveAssignedTasks = async () => {
    
    const payload = [];
    for (const [date, times] of Object.entries(assignedTasks)) {
      for (const [time, task] of Object.entries(times)) {
        const taskData = {
          date,
          time,
          taskName: task.name,
          taskId : task.id,
          processName: task.processName,
          processId: task.processId,
          step : task.step,
          description: task.description,
        };

        // Add break-specific data if it's a break task
        if (task.isBreak) {
          taskData.isBreak = 1;
          taskData.breakStartTime = task.breakStartTime;
          taskData.breakEndTime = task.breakEndTime;
          taskData.breakDuration = task.breakDuration;
          
          // Include additional task data if present
          if (task.additionalTask) {
            taskData.additionalTask = {
              taskName: task.additionalTask.name,
              taskId: task.additionalTask.id,
              processName: task.additionalTask.processName,
              processId: task.additionalTask.processId,
              step: task.additionalTask.step,
              description: task.additionalTask.description
            };
          }
        }

        payload.push(taskData);
      }
    }

    // Tag the last task of each date with the shift's EndTime so the backend
    // can store it directly — no shift lookup needed server-side.
    const uniqueDates = [...new Set(payload.map(t => t.date))];
    for (const date of uniqueDates) {
      const shift = shifts.find((s) => {
        const shiftDateRaw = String(s?.ShiftDate || '');
        const shiftDate = shiftDateRaw.substring(0, 10);
        return shiftDate === date;
      });
      if (!shift || !shift.EndTime) continue;

      const dateTasks = payload.filter(t => t.date === date);
      if (!dateTasks.length) continue;

      const lastTask = dateTasks.reduce((latest, t) =>
        t.time > latest.time ? t : latest, dateTasks[0]
      );
      lastTask.endTime = String(shift.EndTime).substring(0, 8); // e.g. "18:30:00"
    }

    console.log(payload);

    
    const id = staffMember;

    // using useLocation to get the staff id

    

    


    try {
        const res = await fetch('https://api.insightos.co.uk/api/taskcalendarsave', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ payload, id }), // Send as an object with `payload` key
        });

        if (!res.ok) {
            throw new Error('Failed to save tasks.');
        }

        console.log('Tasks saved successfully.');
        setSuccessMessage(`Tasks saved successfully `);
        fetchTasks();



        
    } catch (error) {
        console.error(error);
    }
};






  
  const containerNav = useRef(null);

  // Update assigned tasks
  const handleTaskSelect = (task, time, date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    setAssignedTasks((prev) => ({
      ...prev,
      [dateKey]: {
        ...prev[dateKey],
        [time]: task,
      },
    }));
  };

  useEffect(() => {
    const fetchStaff = async () => {
            try {
        const res = await fetch("https://api.insightos.co.uk/api/staff", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
        });
        if (!res.ok) {
          throw new Error("Failed to fetch staff.");
        }
        const data = await res.json();

        // refresh
        setStaff(data.data);
        if (!staffMember && Array.isArray(data.data) && data.data.length > 0) {
          setStaffMember(String(data.data[0].id));
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchStaff();
  }, []);

    const fetchTasks = async () => {
            if (!staffMember) {
              setShifts([]);
              setMissingDetails([]);
              setNoShift(true);
              setNoMissingTimes(true);
              return;
            }
            setIsLoadingTasks(true);
            try {
        const res = await fetch(`https://api.insightos.co.uk/api/taskcalendar/${staffMember}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
          body: JSON.stringify({
            dates: weekDates.map((date) => format(date, "yyyy-MM-dd")),
          }),
        });
  
        if (!res.ok) {
          throw new Error("Failed to fetch tasks.");
        }
  
        const data = await res.json();
  
        const savedData = data.tasks || []; // Previously saved task data
        const shiftData = data.shifts || []; // Current shifts data
        const breakLength = data.breaklength || 0; // Break length in minutes
        setBreakLength(breakLength);

        // if shiftData is empty set in State no shift booked for this week

        setNoShift(shiftData.length === 0);

        console.log("Data:", shiftData.length);

  
        console.log("Saved Data:", savedData);
        console.log("Shift Data:", shiftData);
  
        const missingDetails = shiftData.map((shift) => {
          const shiftDate = format(new Date(shift.ShiftDate), "yyyy-MM-dd");
          const startTime = shift.StartTime.split(":").map(Number); // Convert to [hour, minute, second]
          const endTime = shift.EndTime.split(":").map(Number); // Convert to [hour, minute, second]
  
          const workingHours = [];
          let currentHour = startTime[0];
  
          // Generate all working hours
          while (currentHour < endTime[0]) {
            workingHours.push(
              `${String(currentHour).padStart(2, "0")}:00:00` // e.g., "09:00:00"
            );
            currentHour++;
          }
  
          // Find missing times for this shift
          const missingTimes = workingHours.filter((hour) => {
            return !savedData.some((saved) => {
              const savedDate = format(new Date(saved.TaskDate), "yyyy-MM-dd");
              return shiftDate === savedDate && hour === saved.StartTime;
            });
          });
  
          return {
            shiftDate,
            missingTimes,
          };
        });
  
        console.log("Missing Details:", missingDetails);

        setMissingDetails(missingDetails);
  
        // Set state or handle missing details as needed
        setShifts(shiftData);
  
        const tasks = data.data || [];

        // Extract Task and Step values, ensuring they are valid
        let taskWithSteps = tasks
          .map(task => ({
            name: task?.Task?.trim(),
            step: task?.Step,
            id : task?.id,
            processName: task?.ProcessName,
            processId: task?.ProcessId,
            description: task?.Description,
          }))
          .filter(task => task.name); // Remove entries with empty Task names
        
        // Remove duplicates based on the task name
        const uniqueTasks = [];
        const taskNames = new Set();
        
        taskWithSteps.forEach(task => {
          if (!taskNames.has(task.name)) {
            taskNames.add(task.name);
            uniqueTasks.push(task);
          }
        });
        
        // Add "Break" with a placeholder Step (adjust as needed)
        uniqueTasks.push({ name: "Break", step: "N/A" });
        
        // Remove "Training" if present
        const filteredTasks = uniqueTasks.filter(task => task.name !== "Training");
        
        // Update state
        setTasks(filteredTasks);

        console.log("Tasks:", filteredTasks);
        
  
        // Optionally display or log missing hours
        missingDetails.forEach(({ shiftDate, missingTimes }) => {
          if (missingTimes.length > 0) {
            console.warn(`On ${shiftDate}, the following times are missing:`, missingTimes);
            setNoMissingTimes(false);
          } else {
            console.log(`No missing times for ${shiftDate}.`);
            if (shiftData.length > 0) {
            setNoMissingTimes(true);
            }
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingTasks(false);
      }
    };
  
   
  
  
    useEffect(() => {
      fetchTasks(); // Fetch new tasks when staff changes
    
      // Clear all assigned tasks when staff member changes
      setAssignedTasks({});
    }, [staffMember, currentWeekStart]); // Runs whenever staff member or week changes
    

        

  const generateTimeSlots = (startTime, endTime, inter) => {
    const slots = [];
    let currentTime = new Date(`1970-01-01T${startTime}`);
    const endDateTime = new Date(`1970-01-01T${endTime}`);
    while (currentTime < endDateTime) {
      // Only add this slot if a full 60-minute block fits before shift end.
      // If less than 60 min remains, the previous task will be extended to
      // shift end by the backend — no stub slot needed.
      const minutesRemaining = (endDateTime - currentTime) / (1000 * 60);
      if (minutesRemaining < 60) break;
      slots.push(format(currentTime, "HH:mm"));
      currentTime.setMinutes(currentTime.getMinutes() + 60);
    }
    return slots;
  };

  // Helper function to calculate shift duration in hours
  const calculateShiftDuration = (startTime, endTime) => {
    const start = new Date(`1970-01-01T${startTime}`);
    const end = new Date(`1970-01-01T${endTime}`);
    return (end - start) / (1000 * 60 * 60); // Convert milliseconds to hours
  };

  // Helper function to get break information for a shift
  const getShiftBreakInfo = (shift) => {
      const duration = calculateShiftDuration(shift.StartTime, shift.EndTime);
      if (duration >= 6) {
        return {
          showBreak: true,
          breakLength: breakLength  // Use breakLength from state
        };
      }
      return { showBreak: false, breakLength: 0 };
    };

  const getShiftsForDate = (date) => {
    return shifts.filter(
      (shift) => format(new Date(shift.ShiftDate), "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
    );
  };

  return (
    <div className="flex h-full flex-col" data-component="task-calendar-builder">
      <ManagerNav />

      {overwrittenTasks.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">⚠ Overwritten Tasks ({overwrittenTasks.length})</span>
            <span className="text-xs text-amber-600">— these tasks were displaced by a meeting and could not fit within the shift</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {overwrittenTasks.map(task => (
              <div key={task.TaskID} className="flex items-center gap-1 bg-amber-100 border border-amber-300 rounded-full px-3 py-1 text-xs text-amber-800">
                <span className="font-medium">{task.Name}</span>
                <span className="text-amber-500">·</span>
                <span>{task.Task}</span>
                {task.TaskDate && <span className="text-amber-500">· {String(task.TaskDate).substring(0, 10)}</span>}
                <button
                  onClick={() => dismissOverwrittenTask(task.TaskID)}
                  className="ml-1 rounded-full w-4 h-4 flex items-center justify-center bg-amber-300 hover:bg-amber-400 text-amber-900 font-bold transition-colors"
                  title="Dismiss (delete this task)"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="container mx-auto px-2 md:px-4 py-4">
        <header className="flex flex-wrap justify-between items-center border-b pb-4 mb-4">
          <h3 className="text-lg font-semibold p-2 bg-gray-100 text-indigo-600 rounded shadow-sm mb-3 sm:mb-0">
            <time dateTime={format(currentWeekStart, "yyyy-MM")}>
              {format(currentWeekStart, "MMMM yyyy")}
            </time>
          </h3>
          
          <div className="flex items-center mb-3 sm:mb-0">
            <button
              onClick={() => setCurrentWeekStart((prev) => addDays(prev, -7))}
              data-component="task-calendar-week-nav"
              data-feature="week-previous"
              className="mr-2 p-2 rounded shadow-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
              aria-label="Previous Week"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() =>
                setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))
              }
              data-component="task-calendar-week-nav"
              data-feature="week-today"
              className="px-4 py-2 text-sm font-semibold text-white shadow-md rounded bg-indigo-600 hover:bg-indigo-700 transition-colors"
            >
               {format(currentWeekStart, "dd MMM yyyy")}
            </button>
            <button
              onClick={() => setCurrentWeekStart((prev) => addDays(prev, 7))}
              data-component="task-calendar-week-nav"
              data-feature="week-next"
              className="ml-2 p-2 rounded shadow-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
              aria-label="Next Week"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="flex items-center mb-3 sm:mb-0">
            <select
              onChange={(e) => setStaffMember(e.target.value)}
              value={staffMember}
              data-component="task-calendar-staff-select"
              className="rounded border border-indigo-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select Staff</option>
              {staff.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.Name} - {member.Role}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full sm:w-auto flex flex-wrap gap-2 mt-3 sm:mt-0">
            <button
              onClick={saveAssignedTasks}
              data-feature="save-calendar-tasks"
              data-component="save-tasks"
              data-time-saved="10"
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
            >
              Save Tasks
            </button>
            
            <button 
              className={`px-4 py-2 text-sm font-semibold text-white shadow-sm rounded transition-colors ${!openTaskGraph ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
              data-feature={!openTaskGraph ? 'open-task-graph' : 'close-task-graph'}
              onClick={() => setOpenTaskGraph(!openTaskGraph)}
            >
              {!openTaskGraph ? 'Task Graph' : 'Close Graph'}
            </button>

            <button 
              className={`px-4 py-2 text-sm font-semibold text-white shadow-sm rounded transition-colors ${!open ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'}`}
              data-feature={!open ? 'open-process-viewer' : 'close-process-viewer'}
              onClick={() => setOpen(!open)}
            >
              {!open ? 'Processes' : 'Close Processes'}
            </button>

            <button 
              className={`px-4 py-2 text-sm font-semibold text-white shadow-sm rounded transition-colors ${!taskOpen ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-red-600 hover:bg-red-700'}`}
              data-feature={!taskOpen ? 'open-team-tasks' : 'close-team-tasks'}
              onClick={() => setTaskOpen(!taskOpen)}
            >
              {!taskOpen ? 'Tasks' : 'Close Tasks'}
            </button>
          </div>
          
          {successMessage && (
            <div className="w-full text-center mt-2">
              <p className="text-sm text-green-600 font-medium">{successMessage}</p>
            </div>
          )}
        </header>

        {open && (
          <div className="w-full mx-auto mb-6 bg-white rounded-lg shadow-md p-4" data-component="task-process-viewer">
            <ProcessViewer />
          </div>
        )}
        
        {taskOpen && (
          <div className="w-full mx-auto mb-6 bg-white rounded-lg shadow-md p-4" data-component="task-team-view">
            <TaskCalendarTeamView />
          </div>
        )}

        {openTaskGraph && (
          <div className="w-full md:w-3/4 lg:w-1/2 mx-auto mb-6 bg-white rounded-lg shadow-md p-4" data-component="task-graph-panel">
            <TaskGraph />
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          {!staffMember ? (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-indigo-800">
              <p className="text-sm font-semibold">Select a staff member to load tasks and shifts.</p>
              <p className="mt-1 text-xs text-indigo-700">Choose someone from the dropdown above to view this week’s calendar.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-indigo-500 font-semibold">Week</p>
                <p className="text-sm font-semibold text-indigo-900">{format(currentWeekStart, "dd MMM")} - {format(addDays(currentWeekStart, 6), "dd MMM yyyy")}</p>
              </div>
              <div className={`rounded-lg border px-3 py-2 ${noShift ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
                <p className={`text-[11px] uppercase tracking-wide font-semibold ${noShift ? 'text-red-500' : 'text-emerald-600'}`}>Shift Status</p>
                <p className={`text-sm font-semibold ${noShift ? 'text-red-700' : 'text-emerald-700'}`}>
                  {noShift ? 'No shifts booked this week' : 'Shifts loaded'}
                </p>
              </div>
              <div className={`rounded-lg border px-3 py-2 ${noMissingTimes ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                <p className={`text-[11px] uppercase tracking-wide font-semibold ${noMissingTimes ? 'text-emerald-600' : 'text-amber-600'}`}>Task Coverage</p>
                <p className={`text-sm font-semibold ${noMissingTimes ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {noMissingTimes ? 'All timeslots assigned' : 'Some timeslots need tasks'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tasks grid */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Available Tasks</h2>
            <span className="text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700 px-2.5 py-1">
              {tasks.length} tasks
            </span>
          </div>
          {!staffMember ? (
            <p className="text-sm text-gray-500">Select a staff member to view matching tasks.</p>
          ) : isLoadingTasks ? (
            <p className="text-sm text-indigo-600">Loading tasks and shifts...</p>
          ) : tasks.length === 0 ? (
            <p className="text-sm text-gray-500">No tasks available for this staff role.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {tasks.map((task, index) => (
                <TaskCard key={`${task.name || 'task'}-${index}`} task={task} />
              ))}
            </div>
          )}
        </div>
 
        {/* Main calendar section */}
        {!noShift && (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Calendar header - days of week */}
            <div className="sticky top-0 z-30 bg-indigo-50 border-b border-indigo-100">
              <div className="grid grid-cols-1 sm:grid-cols-7 divide-y sm:divide-y-0 sm:divide-x divide-indigo-200">
                {weekDates.map((date, index) => {
                  const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
                  return (
                    <div
                      key={index}
                      className={`p-3 ${isToday ? 'bg-indigo-100' : ''}`}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-indigo-800">
                          {format(date, "EEEE")}
                        </span>
                        <span className={`mt-1 inline-flex items-center justify-center w-8 h-8 rounded-full 
                          ${isToday ? 'bg-indigo-600 text-white' : 'text-indigo-600'}`}
                        >
                          {format(date, "d")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
  
            {/* Calendar body - mobile friendly with stacked days for small screens */}
            <div className="block sm:hidden">
              {/* Mobile view - days as vertical stack */}
              {weekDates.map((date) => {
                const dateStr = format(date, "yyyy-MM-dd");
                const missingForDate = missingDetails.find(
                  (detail) => detail.shiftDate === dateStr
                )?.missingTimes || [];
                
                return (
                  <div key={dateStr} className="mb-6 border-b border-gray-200 pb-6">
                    <h3 className="text-lg font-medium text-indigo-800 mb-4 px-4">
                      {format(date, "EEEE, MMMM d")}
                    </h3>
                    {getShiftsForDate(date).length > 0 ? (
                      getShiftsForDate(date).map((shift) => {
                        const breakInfo = getShiftBreakInfo(shift);
                        return (
                          <div key={shift.id} className="space-y-4 px-4">
                            {/* Break information display */}
                            {breakInfo.showBreak && (
                              <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                <div className="flex items-center text-sm text-yellow-700">
                                  <span className="font-medium">Legal Break:</span>
                                  <span className="ml-2">{breakInfo.breakLength} minutes</span>
                                </div>
                              </div>
                            )}
                            {generateTimeSlots(shift.StartTime, shift.EndTime).map((time) => {
                              const isMissing = missingForDate.includes(`${time}:00`);
                              return (
                                <TimeSlot
                                  key={time}
                                  time={time}
                                  date={date}
                                  onTaskSelect={handleTaskSelect}
                                  assignedTask={assignedTasks[format(date, "yyyy-MM-dd")]?.[time]}
                                  isMissing={isMissing}
                                  availableTasks={tasks}
                                  breakLength={breakLength}
                                />
                              );
                            })}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 italic px-4">No shifts scheduled</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop view */}
            <div className="hidden sm:grid sm:grid-cols-7 sm:divide-x divide-gray-100">
              {weekDates.map((date) => {
                const dateStr = format(date, "yyyy-MM-dd");
                const missingForDate = missingDetails.find(
                  (detail) => detail.shiftDate === dateStr
                )?.missingTimes || [];

                return (
                  <div key={dateStr} className="flex flex-col min-h-[200px]">
                    {getShiftsForDate(date).length > 0 ? (
                      getShiftsForDate(date).map((shift) => {
                        const breakInfo = getShiftBreakInfo(shift);
                        return (
                          <div key={shift.id} className="flex-1">
                            {/* Break information display */}
                            {breakInfo.showBreak && (
                              <div className="mx-1 mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                                <div className="text-xs text-yellow-700 text-center">
                                  <div className="font-medium">Legal Break</div>
                                  <div>{breakInfo.breakLength} min</div>
                                </div>
                              </div>
                            )}
                            {generateTimeSlots(shift.StartTime, shift.EndTime).map((time) => {
                              const isMissing = missingForDate.includes(`${time}:00`);
                              return (
                                <TimeSlot
                                  key={time}
                                  time={time}
                                  date={date}
                                  onTaskSelect={handleTaskSelect}
                                  assignedTask={assignedTasks[format(date, "yyyy-MM-dd")]?.[time]}
                                  isMissing={isMissing}
                                  availableTasks={tasks}
                                  breakLength={breakLength}
                                />
                              );
                            })}
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 text-center text-gray-500 italic flex-1 flex items-center justify-center">
                        No shifts scheduled
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  
  
  
}
